// Pure check-in slot maths (architecture sprint-04 §5.5; fixes INV #8). The anchor is server-owned
// and resets on start, switch and resume, so slot 0 holds for a full interval after any of them.

const MS_PER_MINUTE = 60_000;

export interface CheckinInput {
  anchorMs: number;
  intervalMinutes: number;
  serverNowMs: number;
}

export interface CheckinState {
  /** 0 → nothing due. After a sleep through several slots only the latest is reported. */
  slot: number;
  slotId: string | null;
  nextDueMs: number;
}

export function checkinState(i: CheckinInput): CheckinState {
  const period = i.intervalMinutes * MS_PER_MINUTE;
  const slot = Math.max(0, Math.floor((i.serverNowMs - i.anchorMs) / period));
  return {
    slot,
    slotId: slot === 0 ? null : slotIdFor(i.anchorMs, i.intervalMinutes, slot),
    nextDueMs: i.anchorMs + (slot + 1) * period,
  };
}

export function slotIdFor(anchorMs: number, intervalMinutes: number, slot: number): string {
  return `${anchorMs}:${intervalMinutes}:${slot}`;
}

interface ParsedSlot {
  anchorMs: number;
  intervalMinutes: number;
  slot: number;
}

export function parseSlotId(slotId: string | null): ParsedSlot | null {
  if (!slotId) return null;
  const parts = slotId.split(':').map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  const [anchorMs, intervalMinutes, slot] = parts as [number, number, number];
  return { anchorMs, intervalMinutes, slot };
}

/**
 * True when `slotId` is covered by the acknowledged slot: the same id, or the same anchor and
 * interval with a slot at or after it.
 */
export function isAcknowledged(slotId: string, ackSlotId: string | null): boolean {
  if (ackSlotId === slotId) return true;
  const target = parseSlotId(slotId);
  const ack = parseSlotId(ackSlotId);
  if (!target || !ack) return false;
  return ack.anchorMs === target.anchorMs && ack.intervalMinutes === target.intervalMinutes && ack.slot >= target.slot;
}
